import MatestackUiVueJs from "matestack-ui-vuejs";
// Registers the <calendar-date> / <calendar-month> custom elements. Host apps
// must `npm install cally`. The elements are built imperatively in mounted()
// inside a v-pre div, so Vue's template compiler never sees them and hosts
// need no `compilerOptions.isCustomElement`.
import "cally";
import { parseTime, formatTime, timeProblem } from "./time_entry";
import { maskAuDate, unmaskIso, isPartialAuDate } from "./date_entry";

const pad = (n, width = 2) => String(n).padStart(width, "0");

const ISO_PATTERN = /^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2}))?/;

const ICON_ATTRS =
  'xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" width="16" height="16" aria-hidden="true"';
const PREVIOUS_ICON = `<svg slot="previous" ${ICON_ATTRS}><path stroke-linecap="round" stroke-linejoin="round" d="M15.75 19.5 8.25 12l7.5-7.5"/></svg>`;
const NEXT_ICON = `<svg slot="next" ${ICON_ATTRS}><path stroke-linecap="round" stroke-linejoin="round" d="m8.25 4.5 7.5 7.5-7.5 7.5"/></svg>`;

// Builds an ISO date string, or null when y/m/d is not a real calendar date.
// 31/02 is refused rather than rolled over into March.
const isoFromParts = (y, m, d) => {
  if (y < 1000 || y > 9999) return null;
  const date = new Date(2000, 0, 1);
  date.setFullYear(y, m - 1, d);
  if (date.getFullYear() !== y || date.getMonth() !== m - 1 || date.getDate() !== d) return null;
  return `${pad(y, 4)}-${pad(m)}-${pad(d)}`;
};

// Strict DD/MM/YYYY parser. Accepts d/m/yyyy and dd/mm/yyyy with "/", "-" or
// "." separators, ddmmyyyy (8 digits), d/m/yy (read as 20yy) and ISO
// yyyy-mm-dd (unambiguous). Returns an ISO date string, or null.
export const parseAuDate = (text) => {
  const t = String(text || "").trim();
  let m;
  if ((m = /^(\d{4})-(\d{1,2})-(\d{1,2})$/.exec(t))) {
    return isoFromParts(+m[1], +m[2], +m[3]);
  }
  if ((m = /^(\d{1,2})[/.-](\d{1,2})[/.-](\d{4}|\d{2})$/.exec(t))) {
    const year = m[3].length === 2 ? 2000 + +m[3] : +m[3];
    return isoFromParts(year, +m[2], +m[1]);
  }
  if ((m = /^(\d{2})(\d{2})(\d{4})$/.exec(t))) {
    return isoFromParts(+m[3], +m[2], +m[1]);
  }
  return null;
};

export const formatAuDate = (iso) => {
  const m = ISO_PATTERN.exec(iso || "");
  return m ? `${m[3]}/${m[2]}/${m[1]}` : "";
};

const dsFormDateInput = {
  mixins: [MatestackUiVueJs.componentMixin, MatestackUiVueJs.formInputMixin],
  template: MatestackUiVueJs.componentHelpers.inlineTemplate,
  data() {
    return {
      display: "", // what the text field shows (DD/MM/YYYY as typed)
      timeDisplay: "", // what the time field shows (the user's hour cycle), datetimes only
      open: false, // calendar popup
      invalidMessage: "",
    };
  },
  methods: {
    field(name) {
      return this.getTemplateElement().querySelector(`[data-ds-date="${name}"]`);
    },
    withTime() {
      return !!this.props["with_time"];
    },
    cycle() {
      return this.props["hour_cycle"] === "h12" ? "h12" : "h24";
    },
    // The time field as HH:MM: "" when blank, null when it is not a time.
    timeValue() {
      const typed = this.timeDisplay.trim();
      return typed === "" ? "" : parseTime(typed);
    },
    limit(name) {
      const value = this.props[name];
      return value ? String(value).slice(0, 10) : null;
    },
    // The form's value for the current fields, plus per-field problems.
    // `pending` is an unfinished entry (a half-typed year): it is neither
    // accepted nor flagged, but it still blocks a save until it is complete.
    compose(pending = false) {
      const text = this.display.trim();
      const time = this.timeValue();
      const result = { value: "", textMessage: "", timeMessage: "" };
      if (!this.withTime() && text === "") return result;
      if (this.withTime() && text === "" && time === "") return result;
      if (this.withTime() && time === null) result.timeMessage = timeProblem(this.cycle());

      if (text === "") {
        result.textMessage = "Add a date";
        return result;
      }
      if (pending) {
        result.textMessage = "Enter a real date as DD/MM/YYYY";
        return result;
      }
      const iso = parseAuDate(text);
      if (!iso) {
        result.textMessage = "Enter a real date as DD/MM/YYYY";
        return result;
      }
      const min = this.limit("min");
      const max = this.limit("max");
      if (min && iso < min) {
        result.textMessage = `Date must be on or after ${formatAuDate(min)}`;
        return result;
      }
      if (max && iso > max) {
        result.textMessage = `Date must be on or before ${formatAuDate(max)}`;
        return result;
      }
      if (!this.withTime()) {
        result.value = iso;
        return result;
      }
      if (!time) {
        result.timeMessage = result.timeMessage || "Add a time";
        return result;
      }
      result.value = `${iso}T${time}`;
      return result;
    },
    // Recomputes the form value and validity from the fields. `showMessage`
    // also surfaces the problem inline; it always blocks the save through
    // the field's native custom validity. `pending` (an unfinished date being
    // typed) hides the problem until the entry is complete.
    update(showMessage, pending = false) {
      const key = this.props["key"];
      const { value, textMessage, timeMessage } = this.compose(pending);
      const text = this.field("text");
      const time = this.field("time");
      if (text) text.setCustomValidity(textMessage);
      if (time) time.setCustomValidity(timeMessage);
      const message = textMessage || timeMessage;
      if (pending) this.invalidMessage = "";
      else if (showMessage || !message) this.invalidMessage = message;
      if (this.parentFormData[key] !== value) {
        this.parentFormData[key] = value;
        this.inputChanged(key);
      }
      if (this.emitted !== value) {
        this.emitted = value;
        this.getTemplateElement().dispatchEvent(
          new CustomEvent("ds:change", { bubbles: true, detail: { key, value } })
        );
      }
    },
    // Loads an ISO value (from init, a reset or a server rerender) into the
    // fields.
    load(value) {
      const m = ISO_PATTERN.exec(value || "");
      this.display = m ? `${m[3]}/${m[2]}/${m[1]}` : "";
      this.timeDisplay = m && m[4] ? formatTime(`${m[4]}:${m[5]}`, this.cycle()) : "";
      this.emitted = m ? this.compose().value : "";
      this.invalidMessage = "";
      const text = this.field("text");
      const time = this.field("time");
      if (text) text.setCustomValidity("");
      if (time) time.setCustomValidity("");
    },
    afterInitialize(value) {
      this.load(value);
    },
    onText(event) {
      const el = event.target;
      let text = el.value;
      // Insert the slashes as digits are typed, so the digits-only numeric
      // keypad can enter DD/MM/YYYY. Never on deletion, while composing, or
      // with the caret mid-text. Written straight to the element: when the
      // ninth digit arrives `display` is already right, so Vue would not repaint.
      const deleting = /^delete/.test(event.inputType || "");
      const atEnd = el.selectionStart === text.length && el.selectionEnd === text.length;
      if (!deleting && !event.isComposing && atEnd) {
        const unmasked = unmaskIso(text);
        const masked = maskAuDate(unmasked) ?? unmasked;
        if (masked !== text) {
          el.value = masked;
          text = masked;
        }
      }
      this.display = text;
      const typed = text.trim();
      // A half-typed date is neither accepted nor flagged. Otherwise don't
      // nag mid-keystroke; show the message once the entry looks complete or
      // the field has been left (onBlur).
      const pending = isPartialAuDate(typed);
      this.update(!pending && (this.touched || typed.length >= 8), pending);
    },
    onTime(event) {
      this.timeDisplay = event.target.value;
      this.update(this.touched);
    },
    onBlur() {
      this.touched = true;
      const iso = parseAuDate(this.display);
      if (iso) this.display = formatAuDate(iso);
      const time = parseTime(this.timeDisplay);
      if (time) this.timeDisplay = formatTime(time, this.cycle());
      this.update(true);
    },
    onKeydown(event) {
      if (event.key === "ArrowDown" && event.altKey) {
        event.preventDefault();
        this.openCalendar();
      } else if (event.key === "Escape" && this.open) {
        event.preventDefault();
        this.closeCalendar(true);
      }
    },
    toggle() {
      if (this.open) {
        this.closeCalendar(true);
      } else {
        this.openCalendar();
      }
    },
    openCalendar() {
      const cal = this.calendar;
      if (!cal) return;
      const iso = parseAuDate(this.display) || "";
      cal.value = iso;
      cal.min = this.limit("min") || "";
      cal.max = this.limit("max") || "";
      if (iso) cal.focusedDate = iso;
      this.open = true;
      this.$nextTick(() => cal.focus());
    },
    closeCalendar(returnFocus) {
      if (!this.open) return;
      this.open = false;
      if (returnFocus) {
        const button = this.field("button");
        if (button) button.focus();
      }
    },
    // Focus left the component. Close the calendar unless it moved somewhere
    // that still belongs to the field; never pull focus back.
    onFocusOut(event) {
      if (!this.open) return;
      const next = event.relatedTarget;
      // null = focus went nowhere focusable (a click on padding, window blur).
      // The document click handler decides those; do not close here.
      if (!next) return;
      if (this.keepsCalendar(next)) return;
      this.closeCalendar(false);
    },
    // Inside = the popup (shadow DOM included: relatedTarget is retargeted to
    // the calendar host, a light-DOM descendant of the popup), the date text
    // box, or the calendar button. The datetime time box is not inside.
    keepsCalendar(el) {
      const popup = this.field("popup");
      return Boolean((popup && popup.contains(el)) || el === this.field("text") || el === this.field("button"));
    },
    buildCalendar() {
      const mount = this.getTemplateElement().querySelector(".ds-date-field-mount");
      if (!mount || mount.querySelector("calendar-date")) return;
      mount.innerHTML =
        '<calendar-date class="cally" locale="en-AU" first-day-of-week="1">' +
        PREVIOUS_ICON +
        NEXT_ICON +
        "<calendar-month></calendar-month></calendar-date>";
      const cal = mount.querySelector("calendar-date");
      cal.addEventListener("change", () => {
        if (!cal.value) return;
        this.display = formatAuDate(cal.value);
        this.touched = true;
        this.update(true);
        this.open = false;
        const text = this.field("text");
        if (text) text.focus();
      });
      this.calendar = cal;
    },
  },
  mounted() {
    // Plain instance fields, not data(): no reactivity wanted.
    this.calendar = null;
    this.touched = false;
    this.emitted = null;
    this.buildCalendar();

    // Form `reset: true` and server rerenders change the form value from
    // outside; resync the fields when it is not the value we wrote.
    this.stopWatching = this.$watch(
      () => this.parentFormData[this.props["key"]],
      (value) => {
        const current = value == null ? "" : value;
        if (current !== this.emitted) this.load(current);
      }
    );

    this.onDocumentClick = (event) => {
      if (this.open && !this.getTemplateElement().contains(event.target)) this.closeCalendar(false);
    };
    this.onDocumentKeydown = (event) => {
      if (this.open && event.key === "Escape") {
        event.preventDefault();
        this.closeCalendar(true);
      }
    };
    document.addEventListener("click", this.onDocumentClick);
    document.addEventListener("keydown", this.onDocumentKeydown);
  },
  beforeUnmount() {
    document.removeEventListener("click", this.onDocumentClick);
    document.removeEventListener("keydown", this.onDocumentKeydown);
    if (this.stopWatching) this.stopWatching();
  },
};

export default dsFormDateInput;
